import * as React from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { troubleReportService, utilService } from 'core/services'
import { Button, Pagination, Panel, PanelTitle, AvatarImage } from 'components/atoms'
import { IndexFilter } from 'components/molecules/TroubleReport/IndexFilter'
import { IWindow } from '../../../static/interfaces'
import * as constants from '../../../static/constants'
import SidebarLayout from '../_layouts/sidebarLayout'
import { AgentRankTag } from 'components/molecules/Weekly/AgentRankTag'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: IWindow

interface IProps {
  user: any
  statuses: any
}

const Index: React.FC<IProps> = props => {
  const params = new URLSearchParams(window.location.search)
  const { data: user } = utilService.getDataFromJson(props.user)
  const [troubleReports, setTroubleReports] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [pagination, setPagination] = React.useState(undefined)
  const [page, setPage] = React.useState(params.get('page') || 1)
  const [searchParams, setSearchParams] = React.useState({
    start_date: params.get('start_date'),
    end_date: params.get('end_date'),
    statuses: params.getAll('statuses[]'),
    order_by: params.get('order_by') || 'order_by_tour_day_desc',
  })

  const syncTroubleReports = React.useCallback(async () => {
    setIsLoading(true)
    try {
      const { troubles, pagination: meta } = await troubleReportService.getTroubleReports(
        searchParams,
        page
      )
      setTroubleReports(troubles)
      setPagination(meta)
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }, [page, searchParams])

  const onChangePageHandler = newPage => {
    const currentParams = new URLSearchParams(window.location.search)
    currentParams.set('page', newPage)

    location.href = `${location.pathname}?${currentParams.toString()}`
  }

  React.useEffect(() => {
    syncTroubleReports()
  }, [])

  return (
    <SidebarLayout user={user}>
      <Wrapper>
        <Panel marginType="dashboard" title={<PanelTitle title="トラブル報告" />}>
          <div className="PanelBody">
            <IndexFilter statuses={props.statuses} searchParams={searchParams} />
            <div className="Items">
              {!isLoading &&
                troubleReports.length > 0 &&
                troubleReports.map(troubleReport => (
                  <div className="Item" key={troubleReport.id}>
                    <div className="tourGroup">
                      <div className="tourStatus">
                        <div className={`status ${troubleReport.status}`}>
                          {troubleReport.status_i18n}
                        </div>
                      </div>

                      <div className="tourInfo">
                        <div className="tourGroupInner">
                          <div className="tourGroupId">
                            id:<span>{troubleReport.id}</span>
                          </div>
                          <div className="tourDate">
                            {/* start_at はサーバー側で I18n.l 済みのため ja ロケールでは moment がパースできない。
                                ロケール非依存の day / start_time から組み立てる */}
                            {moment(
                              `${troubleReport.tour.day} ${troubleReport.tour.start_time}`,
                              'YYYY-MM-DD HH:mm'
                            ).format('YYYY-MM-DD(ddd) HH:mm')}
                          </div>
                        </div>
                        <a
                          href={`/agent/tours/weekly?start=${troubleReport.tour.day}&tour_id=${troubleReport.tour_id}`}
                          className="tourName"
                        >
                          {troubleReport.tour.post.short_name}
                          <SvgIcon
                            name="chevron_right"
                            className="icon"
                            ariaHidden={true}
                            role="presentation"
                          />
                        </a>
                      </div>
                    </div>
                    <div className="troubleReport">
                      <div className="troubleReportHeader">
                        <div className="guide">
                          <a href={`/agent/guides/${troubleReport.guide_id}/edit`}>
                            <AvatarImage size={24} avatarURL={troubleReport.guide.avatar_url} />
                          </a>
                          <div>{troubleReport.guide.username}</div>
                          <div className="agentRank">
                            <AgentRankTag agentRank={troubleReport.guide.agent_rank_as_guide} />
                          </div>
                        </div>
                        <Button small={true}>
                          <a href={`/agent/trouble_reports/${troubleReport.id}`}>詳細</a>
                        </Button>
                      </div>
                      <div className="reportContent">
                        <div className="reportLabel">トラブル内容:</div>
                        <div className="reportText">{troubleReport.trouble_type_i18n || '-'}</div>
                      </div>
                      {troubleReport.guest_diff &&
                        (() => {
                          const { reservation: r, reported: rp } = troubleReport.guest_diff
                          const d = {
                            adults: r.adults !== rp.adults,
                            children: r.children !== rp.children,
                            infants: r.infants !== rp.infants,
                          }
                          return (
                            <div className="guestDiff">
                              <div className="guestDiffLabel">ゲスト人数 (大人 / 子供 / 幼児):</div>
                              <div className="guestDiffValues">
                                <div className="guestDiffRow">
                                  <span className="guestDiffRowLabel">予約:</span>
                                  <span className={d.adults ? 'diff-bold' : ''}>
                                    {r.adults}
                                  </span> /{' '}
                                  <span className={d.children ? 'diff-bold' : ''}>
                                    {r.children}
                                  </span>{' '}
                                  /{' '}
                                  <span className={d.infants ? 'diff-bold' : ''}>{r.infants}</span>
                                </div>
                                <div className="guestDiffRow">
                                  <span className="guestDiffRowLabel">報告:</span>
                                  <span className={d.adults ? 'diff' : ''}>{rp.adults}</span> /{' '}
                                  <span className={d.children ? 'diff' : ''}>{rp.children}</span> /{' '}
                                  <span className={d.infants ? 'diff' : ''}>{rp.infants}</span>
                                </div>
                              </div>
                            </div>
                          )
                        })()}
                      {troubleReport.memo && (
                        <div className="memoContent">
                          <div className="memoLabel">メモ:</div>
                          <div className="memoText">{troubleReport.memo}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              {!isLoading && troubleReports.length === 0 && (
                <div className="noResults">No results</div>
              )}
            </div>
          </div>
          {pagination && (
            <Pagination
              onChangePageHandler={onChangePageHandler}
              currentPage={pagination.current_page}
              prevPage={pagination.prev_page}
              nextPage={pagination.next_page}
              totalCount={pagination.total_count}
              totalPages={pagination.total_pages}
            />
          )}
        </Panel>
      </Wrapper>
    </SidebarLayout>
  )
}

const Wrapper = styled.div`
  .Panel {
    overflow: visible;
  }

  .Items {
    display: flex;
    flex-direction: column;

    .noResults {
      font-size: 20px;
      margin: 60px;
      text-align: center;
      font-weight: bold;
    }

    .Item {
      display: flex;
      flex-wrap: wrap;
      padding: 2.4rem 0;
      border-bottom: solid 1px ${constants.COLORS.divider};
      .tourGroup {
        flex: 0 0 auto;
        width: 28rem;
        padding-right: 1.6rem;
        @media (max-width: ${constants.BREAKPOINT_TABLET}px) {
          width: 100%;
          padding-right: 0;
        }
        .tourInfo,
        .tourStatus {
          font-size: 1.4rem;
          margin-bottom: 0.8rem;
          .tourGroupInner > div {
            margin-bottom: 0.4rem;
          }
          .tourGroupId span {
            font-weight: bold;
            margin-right: 0.8rem;
          }
          .tourDate {
            color: ${constants.COLORS.black};
            margin-right: 0.8rem;
          }
        }
        .tourName {
          display: flex;
          align-items: center;
          line-height: 2.4rem;
          font-size: 1.6rem;
          color: ${constants.COLORS.primary};
          text-decoration: underline;
          &:hover {
            text-decoration: none;
          }
        }
        .tourStatus {
          display: flex;
          align-items: center;
          gap: 1.2rem;
          .status {
            padding: 0.4rem 1.2rem;
            font-size: 1.4rem;
            font-weight: bold;
            border: 1px solid;

            &.pending {
              color: ${constants.COLORS.danger};
              background-color: ${constants.COLORS.dangerBG};
              border-color: ${constants.COLORS.dangerBorder};
            }

            &.in_progress {
              color: ${constants.COLORS.infoText};
              background-color: ${constants.COLORS.infoBG};
              border-color: ${constants.COLORS.infoBorder};
            }

            &.resolved {
              color: ${constants.COLORS.primary};
              background-color: ${constants.COLORS.successBG2};
              border-color: ${constants.COLORS.successBorder2};
            }

            &.closed {
              color: ${constants.COLORS.subText};
              background-color: ${constants.COLORS.lightGray};
              border-color: ${constants.COLORS.divider2};
            }
          }
        }
      }

      .troubleReport {
        flex: 1 1 auto;
        width: calc(100% - 28rem);
        border: solid 1px ${constants.COLORS.divider2};
        padding: 1.6rem;
        margin: 0;
        border-radius: 0.4rem;
        box-shadow: 0 0.1rem 0.4rem rgba(0, 0, 0, 0.1);
        @media (max-width: ${constants.BREAKPOINT_TABLET}px) {
          width: 100%;
        }
        .troubleReportHeader {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.8rem;
        }
        .guide {
          display: flex;
          align-items: center;
          div {
            color: ${constants.COLORS.black};
            font-size: 1.4rem;
            margin-left: 0.4rem;
          }
          .agentRank {
            div {
              font-size: 12px;
            }
          }
        }
        .reportContent,
        .guestDiff,
        .memoContent {
          margin-top: 0.8rem;
          font-size: 1.4rem;
          .reportLabel,
          .guestDiffLabel,
          .memoLabel {
            font-weight: bold;
            color: ${constants.COLORS.subText};
            margin-bottom: 0.4rem;
          }
          .reportText,
          .memoText {
            white-space: pre-wrap;
            word-break: break-word;
          }
        }
        .guestDiff {
          .guestDiffValues {
            display: flex;
            flex-direction: column;
            gap: 0.4rem;
            .guestDiffRow {
              display: flex;
              gap: 0.8rem;
              .guestDiffRowLabel {
                color: ${constants.COLORS.subText};
                width: 4rem;
              }
              span {
                &.diff-bold {
                  font-weight: bold;
                }
                &.diff {
                  font-weight: bold;
                  color: ${constants.COLORS.danger};
                }
              }
            }
          }
        }
        .memoContent {
          margin-top: 1.2rem;
          padding-top: 1.2rem;
          border-top: 1px dashed ${constants.COLORS.divider2};
        }
      }
    }
  }
`

export default Index
